/**
 * Tells the user when a pending account deletion was undone, or is still live.
 *
 * Iter 118.27. Two states, and the FIRST one is the important one:
 *
 * 1. `cancelledOnLogin` — their deletion was just reversed because they signed
 *    back in. This is what the delete dialog promises ("logging back in will
 *    cancel the deletion"), so the reversal is correct — but doing it SILENTLY
 *    is its own trap. Someone who asked to be deleted and later signs in for any
 *    reason, or having forgotten, walks away still believing the account is gone.
 *    They only discover otherwise if they notice their data is still there.
 *
 *    So we say it plainly, and offer the way back to deletion in the same
 *    breath — because the most likely reason to see this notice is that the
 *    cancellation was NOT what they wanted.
 *
 * 2. `scheduledDeletionAt` still set while holding a session. Normally
 *    impossible: request-deletion deletes every session, and creating one
 *    cancels the deletion, so the two states cannot coexist. Handled anyway for
 *    the case where the session delete fails mid-request — a user silently
 *    counting down to deletion with no way to see or stop it is exactly the bug
 *    this whole area was fixing, and leaving it invisible would repeat it.
 *
 * Dismissal is per-session (sessionStorage), not permanent: this concerns
 * whether the account still exists, which is worth re-surfacing on a new visit
 * if it is somehow still true.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'accountDeletionNoticeDismissed';

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch (_) { return null; }
};

const AccountDeletionNotice = ({ cancelledOnLogin, scheduledDeletionAt, onNavigateToAccount }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
  });
  const [cancelling, setCancelling] = useState(false);
  const [cancelledNow, setCancelledNow] = useState(false);

  const pending = !!scheduledDeletionAt && !cancelledNow;
  if (dismissed || (!cancelledOnLogin && !pending)) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* noop */ }
  };

  const cancelDeletion = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/auth/account/cancel-deletion`);
      setCancelledNow(true);
      toast.success('Your account deletion has been cancelled.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel the deletion');
    } finally { setCancelling(false); }
  };

  // The still-pending case is the more urgent of the two, so it wins.
  const isPending = pending;
  const when = formatDate(scheduledDeletionAt);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mx-auto max-w-2xl mt-3 mb-1 px-4`}
      data-testid="account-deletion-notice"
    >
      <div
        className={`rounded-xl border p-3.5 flex items-start gap-3 ${
          isPending
            ? 'bg-[#FBF6EE] border-[#D4A574]/40'
            : 'bg-white border-[#E8E6DF]/70'
        }`}
      >
        <AlertCircle
          size={17}
          className={`flex-shrink-0 mt-0.5 ${isPending ? 'text-[#A67C3E]' : 'text-[#4E8B7C]'}`}
        />
        <div className="flex-1 min-w-0">
          {isPending ? (
            <>
              <p className="text-sm font-medium text-[#2C3333]">
                Your account is scheduled for deletion
                {when ? ` on ${when}` : ''}
              </p>
              <p className="text-xs text-[#5C6666] mt-0.5 leading-relaxed">
                Everything is still here until then. You can stop it now.
              </p>
              <button
                onClick={cancelDeletion}
                disabled={cancelling}
                className="mt-2.5 text-xs font-medium bg-[#4E8B7C] text-white rounded-full px-4 py-1.5 hover:bg-[#3D7265] inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="deletion-notice-cancel-btn"
              >
                {cancelling && <Loader2 size={12} className="animate-spin" />}
                Keep my account
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[#2C3333]">
                Welcome back — your account deletion was cancelled
              </p>
              <p className="text-xs text-[#5C6666] mt-0.5 leading-relaxed">
                Signing back in stops a scheduled deletion, so nothing has been
                removed. If you still want your account deleted, you can request
                it again in Account &amp; Security.
              </p>
              {onNavigateToAccount && (
                <button
                  onClick={() => { dismiss(); onNavigateToAccount(); }}
                  className="mt-2 text-xs font-medium text-[#4E8B7C] hover:text-[#3D7265] underline"
                  data-testid="deletion-notice-account-link"
                >
                  Open Account &amp; Security
                </button>
              )}
            </>
          )}
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-full text-[#8C9696] hover:bg-[#E8E6DF]/40 flex-shrink-0"
          aria-label="Dismiss"
          data-testid="deletion-notice-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </motion.div>
  );
};

export default AccountDeletionNotice;
